// src/admin/components/AdminLayout.tsx
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  CircularProgress,
  Skeleton,
  Collapse,
  Tooltip,
  Switch,
} from '@mui/material';
import {
  Logout,
  AccountCircle,
  Menu as MenuIcon,
  Settings,
  ExpandLess,
  ExpandMore,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { GRADIENTS, SHADOWS, BORDER_RADIUS } from '../../theme/themeConfig';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useThemeMode } from '../../theme/Thememodecontext';
import {
  getNavGroupsForPermissions,
  NavGroup,
} from '../components/rolePermissions';
import AdminAIChat from './AdminAIChat';

const drawerWidth = 280;

// ── Helper: find which group contains the current path ────────────────────────
const getActiveGroupId = (pathname: string, groups: NavGroup[]): string | null => {
  for (const group of groups) {
    if (group.items.some((item) => item.path === pathname)) return group.id;
  }
  return null;
};

const AdminLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingOrdersCount, setPendingOrdersCount] = useState<number>(0);
  const [loadingOrdersCount, setLoadingOrdersCount] = useState<boolean>(true);

  const { logout, currentUser, userProfile, profileLoading, isSuperAdmin } = useAuth();
  const { companyProfile, loading: companyLoading } = useCompany();
  const { isDark, toggleTheme, colors, shadows } = useThemeMode();

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Build nav groups from live permissions ─────────────────────────────────
  // When isSuperAdmin is true, getNavGroupsForPermissions returns ALL_NAV_GROUPS
  // without any filtering — the super-admin sees every menu item.
  const permissions = userProfile?.permissions ?? [];
  const navGroups = getNavGroupsForPermissions(permissions, isSuperAdmin);

  // ── Open/close state for sidebar groups ────────────────────────────────────
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>(() => {
    const activeGroup = getActiveGroupId(location.pathname, navGroups);
    return navGroups.reduce(
      (acc, group) => {
        acc[group.id] =
          group.id === activeGroup ||
          (activeGroup === null && group.id === navGroups[0]?.id);
        return acc;
      },
      {} as { [key: string]: boolean }
    );
  });

  // Auto-expand group containing active route when route changes
  useEffect(() => {
    const activeGroup = getActiveGroupId(location.pathname, navGroups);
    if (activeGroup) {
      setOpenGroups((prev) => {
        if (prev[activeGroup]) return prev;
        return { ...prev, [activeGroup]: true };
      });
    }
  }, [location.pathname]);

  // Re-initialise open groups when permissions change (e.g. after role update)
  useEffect(() => {
    const activeGroup = getActiveGroupId(location.pathname, navGroups);
    setOpenGroups(
      navGroups.reduce(
        (acc, group) => {
          acc[group.id] =
            group.id === activeGroup ||
            (activeGroup === null && group.id === navGroups[0]?.id);
          return acc;
        },
        {} as { [key: string]: boolean }
      )
    );
  }, [permissions.join(','), isSuperAdmin]); // re-run when permissions or super-admin status changes

  // ── Pending orders badge ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPendingOrdersCount = async () => {
      try {
        setLoadingOrdersCount(true);
        const ordersRef = collection(db, 'orders');
        const pendingQuery = query(ordersRef, where('status', '==', 'pending'));
        const snapshot = await getCountFromServer(pendingQuery);
        setPendingOrdersCount(snapshot.data().count);
      } catch (error) {
        console.error('Error fetching pending orders count:', error);
        setPendingOrdersCount(0);
      } finally {
        setLoadingOrdersCount(false);
      }
    };

    fetchPendingOrdersCount();
    const interval = setInterval(fetchPendingOrdersCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Inject badge into the Order item
  const navGroupsWithBadge = navGroups.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      if (item.path === '/admin/orders') {
        return {
          ...item,
          badge: pendingOrdersCount > 0 ? `+${pendingOrdersCount}` : undefined,
          badgeLoading: loadingOrdersCount,
        };
      }
      return { ...item, badge: undefined, badgeLoading: false };
    }),
  }));

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleGroupToggle = (groupId: string) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleSettingClick = () => {
    handleMenuClose();
    navigate('/admin/settings');
  };
  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/admin/profile');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
    handleMenuClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  // ── Display helpers ─────────────────────────────────────────────────────────
  const getUserDisplayName = () => {
    if (userProfile?.displayName) return userProfile.displayName;
    if (currentUser?.email) return currentUser.email.split('@')[0];
    return 'User';
  };

  const getUserShortName = () => getUserDisplayName().split(' ')[0];

  const getAvatarContent = () => {
    if (profileLoading)
      return <CircularProgress size={20} sx={{ color: colors.neutral.white }} />;
    if (userProfile?.photoURL) return null;
    if (userProfile?.displayName) return userProfile.displayName.charAt(0).toUpperCase();
    if (currentUser?.email) return currentUser.email.charAt(0).toUpperCase();
    return 'U';
  };

  const getCompanyLogo = () => {
    if (companyLoading)
      return <CircularProgress size={20} sx={{ color: colors.neutral.white }} />;
    if (companyProfile?.logo) {
      return (
        <Box
          component="img"
          src={companyProfile.logo}
          alt={companyProfile.companyName || 'Logo'}
          sx={{ width: 40, height: 40, borderRadius: 2, objectFit: 'cover' }}
        />
      );
    }
    const initial = companyProfile?.companyName?.charAt(0).toUpperCase() || 'M';
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          background: GRADIENTS.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h6" sx={{ color: colors.neutral.white, fontWeight: 'bold' }}>
          {initial}
        </Typography>
      </Box>
    );
  };

  const getSubscriptionPlan = () => {
    if (companyLoading) return <Skeleton width={40} />;
    if (userProfile?.roleName) return userProfile.roleName.toUpperCase();
    if (userProfile?.role) return userProfile.role.toUpperCase();
    return '--';
  };

  // ── Drawer content ──────────────────────────────────────────────────────────
  const drawer = (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: isDark
          ? `linear-gradient(180deg, ${colors.background.default} 0%, ${colors.background.paper} 100%)`
          : `linear-gradient(180deg, ${colors.background.default} 0%, ${colors.background.paper} 100%)`,
        borderRight: `1px solid ${colors.neutral.gray[100]}`,
      }}
    >
      {/* Brand */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {getCompanyLogo()}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {companyLoading ? (
            <>
              <Skeleton width="80%" height={24} />
              <Skeleton width="40%" height={20} sx={{ mt: 0.5 }} />
            </>
          ) : (
            <>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: colors.neutral.gray[800],
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {companyProfile?.companyName || 'My Company'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.neutral.gray[500],
                  backgroundColor: colors.neutral.gray[100],
                  px: 1,
                  py: 0.2,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  display: 'inline-block',
                }}
              >
                {getSubscriptionPlan()}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Divider sx={{ mx: 2, mb: 1, borderColor: colors.neutral.gray[200], flexShrink: 0 }} />

      {/* Scrollable nav */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 2,
          pb: 2,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: colors.neutral.gray[300],
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': { background: colors.neutral.gray[400] },
        }}
      >
        {profileLoading ? (
          // Skeleton placeholders while permissions are loading
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={42} sx={{ mb: 0.5, borderRadius: 1.5 }} />
          ))
        ) : navGroupsWithBadge.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              No menu items available for your role.
            </Typography>
          </Box>
        ) : (
          navGroupsWithBadge.map((group) => (
            <Box key={group.id} sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleGroupToggle(group.id)}
                sx={{
                  borderRadius: 1.5,
                  py: 1,
                  px: 2,
                  mb: 0.5,
                  minHeight: 40,
                  '&:hover': { backgroundColor: colors.neutral.gray[50] },
                  '&:focus': { outline: 'none' },
                  '&:focus-visible': {
                    outline: `2px solid ${colors.primary.main}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: colors.neutral.gray[500],
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.7rem',
                    flex: 1,
                  }}
                >
                  {group.label}
                </Typography>
                {openGroups[group.id] ? (
                  <ExpandLess sx={{ fontSize: 18, color: colors.neutral.gray[500] }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 18, color: colors.neutral.gray[500] }} />
                )}
              </ListItemButton>

              <Collapse in={openGroups[group.id]} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {group.items.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                      <Tooltip title={item.text} placement="right" arrow>
                        <ListItemButton
                          selected={location.pathname === item.path}
                          onClick={() => handleNavigation(item.path)}
                          sx={{
                            borderRadius: 1.5,
                            py: 1.2,
                            px: 2,
                            minHeight: 42,
                            transition: 'all 0.2s ease-in-out',
                            '&:focus': { outline: 'none' },
                            '&:focus-visible': {
                              outline: `2px solid ${colors.primary.main}`,
                              outlineOffset: '2px',
                            },
                            '&.Mui-selected': {
                              backgroundColor: colors.primary.main,
                              color: colors.neutral.white,
                              '&:hover': { backgroundColor: colors.primary.dark },
                              '& .MuiListItemIcon-root': { color: colors.neutral.white },
                            },
                            '&:hover': {
                              backgroundColor:
                                location.pathname === item.path
                                  ? colors.primary.dark
                                  : colors.neutral.gray[50],
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color:
                                location.pathname === item.path
                                  ? colors.neutral.white
                                  : colors.neutral.gray[500],
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.9rem',
                              fontWeight: location.pathname === item.path ? 600 : 400,
                              color:
                                location.pathname === item.path
                                  ? colors.neutral.white
                                  : colors.neutral.gray[700],
                            }}
                          />
                          {item.badgeLoading ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: colors.neutral.gray[400] }}
                            />
                          ) : (
                            item.badge && (
                              <Chip
                                label={item.badge}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  backgroundColor: colors.status.error,
                                  color: colors.neutral.white,
                                  minWidth: 28,
                                  fontWeight: 600,
                                }}
                              />
                            )
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          ))
        )}
      </Box>

      {/* Dark mode toggle */}
      <Divider sx={{ mx: 2, borderColor: colors.neutral.gray[200] }} />
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isDark ? (
            <DarkMode sx={{ fontSize: 18, color: colors.primary.main }} />
          ) : (
            <LightMode sx={{ fontSize: 18, color: colors.neutral.gray[500] }} />
          )}
          <Typography
            variant="body2"
            sx={{ color: colors.neutral.gray[600], fontSize: '0.85rem', fontWeight: 500 }}
          >
            {isDark ? 'Dark Mode' : 'Light Mode'}
          </Typography>
        </Box>
        <Switch
          checked={isDark}
          onChange={toggleTheme}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary.main },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: colors.primary.main,
            },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{ display: 'flex', minHeight: '100vh', backgroundColor: colors.background.default }}
    >
      <CssBaseline />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: colors.background.paper,
          color: colors.neutral.gray[800],
          borderBottom: `1px solid ${colors.neutral.gray[100]}`,
          borderRadius: 0,
          boxShadow: shadows.sm,
          zIndex: (theme) => theme.zIndex.drawer - 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                display: { md: 'none' },
                flexShrink: 0,
                color: colors.neutral.gray[600],
                '&:focus': { outline: 'none' },
                '&:focus-visible': {
                  outline: `2px solid ${colors.primary.main}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              noWrap
              component="div"
              sx={{
                fontWeight: 'bold',
                color: colors.neutral.gray[800],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Hi, {getUserShortName()} 👋
              </Box>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Hi, {getUserDisplayName()} 👋
              </Box>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Tooltip
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              arrow
            >
              <IconButton
                onClick={toggleTheme}
                size="medium"
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  color: colors.neutral.gray[600],
                  '&:hover': { backgroundColor: colors.neutral.gray[100] },
                  '&:focus': { outline: 'none' },
                  '&:focus-visible': {
                    outline: `2px solid ${colors.primary.main}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                {isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
              </IconButton>
            </Tooltip>

            <IconButton
              size="large"
              onClick={handleMenuClick}
              color="inherit"
              sx={{
                '&:hover': { backgroundColor: colors.neutral.gray[50] },
                '&:focus': { outline: 'none' },
                '&:focus-visible': {
                  outline: `2px solid ${colors.primary.main}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <Avatar
                src={userProfile?.photoURL || undefined}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: colors.primary.main,
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                }}
              >
                {getAvatarContent()}
              </Avatar>
            </IconButton>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{
                mt: 1,
                '& .MuiPaper-root': {
                  borderRadius: BORDER_RADIUS.md,
                  boxShadow: shadows.lg,
                  border: `1px solid ${colors.neutral.gray[200]}`,
                  minWidth: 200,
                  backgroundColor: colors.background.paper,
                },
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${colors.neutral.gray[100]}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: colors.neutral.gray[800] }}
                >
                  {userProfile?.displayName || 'User'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: colors.neutral.gray[500], display: 'block', mt: 0.5 }}
                >
                  {currentUser?.email}
                </Typography>
                {/* Show dynamic role name with fallback to legacy role */}
                {(userProfile?.roleName || userProfile?.role) && (
                  <Chip
                    label={(userProfile.roleName || userProfile.role || '').toUpperCase()}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      mt: 1,
                      backgroundColor: colors.primary.main + '20',
                      color: colors.primary.main,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>

              <MenuItem
                onClick={handleProfileClick}
                sx={{
                  py: 1.5,
                  color: colors.neutral.gray[700],
                  '&:hover': { backgroundColor: colors.neutral.gray[50] },
                  '&:focus': { outline: 'none', backgroundColor: colors.neutral.gray[50] },
                  '&:focus-visible': {
                    outline: `2px solid ${colors.primary.main}`,
                    outlineOffset: '-2px',
                  },
                }}
              >
                <AccountCircle sx={{ mr: 2, color: colors.neutral.gray[600] }} />
                Profile
              </MenuItem>

              {(isSuperAdmin || (userProfile?.permissions ?? []).includes('settings.view')) && (
                <MenuItem
                  onClick={handleSettingClick}
                  sx={{
                    py: 1.5,
                    color: colors.neutral.gray[700],
                    '&:hover': { backgroundColor: colors.neutral.gray[50] },
                    '&:focus': { outline: 'none', backgroundColor: colors.neutral.gray[50] },
                    '&:focus-visible': {
                      outline: `2px solid ${colors.primary.main}`,
                      outlineOffset: '-2px',
                    },
                  }}
                >
                  <Settings sx={{ mr: 2, color: colors.neutral.gray[600] }} />
                  Settings
                </MenuItem>
              )}

              <Divider sx={{ borderColor: colors.neutral.gray[100] }} />

              <MenuItem
                onClick={toggleTheme}
                sx={{
                  py: 1.5,
                  color: colors.neutral.gray[700],
                  '&:hover': { backgroundColor: colors.neutral.gray[50] },
                  '&:focus': { outline: 'none', backgroundColor: colors.neutral.gray[50] },
                  '&:focus-visible': {
                    outline: `2px solid ${colors.primary.main}`,
                    outlineOffset: '-2px',
                  },
                }}
              >
                {isDark ? (
                  <LightMode sx={{ mr: 2, color: colors.neutral.gray[600] }} />
                ) : (
                  <DarkMode sx={{ mr: 2, color: colors.neutral.gray[600] }} />
                )}
                {isDark ? 'Light Mode' : 'Dark Mode'}
              </MenuItem>

              <Divider sx={{ borderColor: colors.neutral.gray[100] }} />

              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1.5,
                  color: colors.neutral.gray[700],
                  '&:hover': {
                    backgroundColor: colors.status.error + '10',
                    color: colors.status.error,
                  },
                  '&:focus': {
                    outline: 'none',
                    backgroundColor: colors.status.error + '10',
                    color: colors.status.error,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${colors.primary.main}`,
                    outlineOffset: '-2px',
                  },
                }}
              >
                <Logout sx={{ mr: 2, color: 'inherit' }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: shadows.lg,
              backgroundColor: colors.background.paper,
              height: '100vh',
              position: 'fixed',
              '&:focus': { outline: 'none' },
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          height: { md: '100vh' },
          overflow: { xs: 'auto', md: 'hidden' },
          backgroundColor: colors.background.default,
        }}
      >
        <Toolbar sx={{ flexShrink: 0 }} />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: { xs: 'visible', md: 'hidden' },
            minHeight: 0,
            p: { xs: 1.5, sm: 2, md: 0 },
          }}
        >
          <Outlet />
        </Box>
      </Box>

      <AdminAIChat />
    </Box>
  );
};

export default AdminLayout;